"use client";

import * as React from "react";
import { Icon } from "@/components/ui/icon";
import { Container } from "@/app/_components/layout/container";
import { problemBlock } from "@/lib/content/homepage";

const iconMap = {
  "alert-triangle": "warning",
  scale: "balance",
  "trending-down": "trending_down",
  "log-out": "logout",
} as const;

function renderWithBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-bold text-clarus-dark">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function useScrollProgress(
  ref: React.RefObject<HTMLElement | null>,
  scrollSpan: number,
  startOffset: number,
): number {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let raf = 0;
    const compute = () => {
      raf = 0;
      const rect = node.getBoundingClientRect();
      const traveled = startOffset - rect.top;
      const p = Math.max(0, Math.min(1, traveled / scrollSpan));
      setProgress(p);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref, scrollSpan, startOffset]);

  return progress;
}

export function ProblemCards() {
  const sectionRef = React.useRef<HTMLElement>(null);
  const cardCount = problemBlock.cards.length;
  const SCROLL_PER_CARD = 0.35;
  const scrollSpan =
    typeof window === "undefined"
      ? 1000
      : window.innerHeight * SCROLL_PER_CARD * cardCount;
  const STICKY_OFFSET = 80;
  const rawProgress = useScrollProgress(sectionRef, scrollSpan, STICKY_OFFSET);

  const [isDesktop, setIsDesktop] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const progress = isDesktop ? rawProgress : 1;
  const REVEAL_END = 0.9;

  return (
    <section
      ref={sectionRef}
      aria-label="Risiken"
      style={
        isDesktop
          ? {
              minHeight: `calc(${SCROLL_PER_CARD * cardCount * 100}vh + 28rem)`,
            }
          : undefined
      }
      className="relative bg-clarus-rose text-clarus-dark [clip-path:inset(0)]"
    >
      {/* `top-20` pins the block just below the sticky header. The block
          contains BOTH the heading and the cards so the heading stays
          visible above the animation — cards slide up underneath. */}
      <div className="sticky top-20 pb-16 pt-12 md:pb-28 md:pt-20">
        <Container>
          <div className="max-w-3xl">
            <h2 className="text-display-lg text-clarus-dark">
              {problemBlock.heading}
            </h2>
            <p className="text-subline mt-3 text-clarus-dark/80 sm:mt-4">
              {problemBlock.intro}
            </p>
          </div>
        </Container>
        <Container className="mt-8 md:mt-12">
          <ul className="-mx-4 grid grid-cols-2 gap-1 sm:mx-0 sm:gap-6 lg:grid-cols-4">
            {problemBlock.cards.map((card, index) => {
                const iconName = iconMap[card.icon as keyof typeof iconMap];
                const local = Math.min(1, progress / REVEAL_END);
                const eased = 1 - Math.pow(1 - local, 3);
                const STEP = 110;
                const maxStart = (cardCount - 1) * STEP;
                const risen = eased * maxStart;
                const translateY = Math.max(0, index * STEP - risen);

                return (
                  <li
                    key={card.title}
                    className="will-change-transform motion-reduce:!transform-none"
                    style={{
                      transform: `translate3d(0, ${translateY}%, 0)`,
                    }}
                  >
                    <div className="flex h-full flex-col items-center rounded-card border-[1.5px] border-clarus-dark bg-white px-4 py-6 text-center text-clarus-dark transition-[transform,box-shadow] duration-200 ease-[ease] hover:-translate-y-1 hover:shadow-[4px_4px_0_0_var(--color-clarus-dark)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:px-6 sm:py-8">
                      <Icon
                        name={iconName}
                        className="text-[24px] text-clarus-dark sm:text-[28px]"
                      />
                      <h3
                        className="mt-4 hyphens-auto break-words text-[0.95rem] font-extrabold leading-tight text-clarus-dark sm:mt-5 sm:text-[1.25rem]"
                        lang="de"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        {card.title}
                      </h3>
                      <p className="mt-2 text-[0.75rem] leading-snug text-clarus-dark/80 sm:text-body sm:mt-3">
                        {renderWithBold(card.body)}
                      </p>
                    </div>
                  </li>
                );
              })}
          </ul>
        </Container>
      </div>
    </section>
  );
}
